import { getDate } from "./dateParser";
import moment from "moment";
import "moment-timezone";
import { DateUtils } from "./dateUtils";

export const dataFormatters = {
  /**
   * Converts moment date provided in source timezone to a valid moment date in specific timezone to start of the day.
   * @param {string | Date | moment.Moment} date source date in source timezone
   * @param {string} timezone timezone string according to moment (See {@link https://gist.github.com/diogocapela/12c6617fc87607d11fd62d2a4f42b02a})
   * @returns {moment.Moment | null} return null if date is invalid, otherwise returns moment date object in specific timezone
   */
  getMomentDateInDifferentTimezone(date, targetTimezone) {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return null;
    }

    tempDate = moment(date);
    let dateNum = tempDate.date();
    let month = tempDate.month();
    let year = tempDate.year();
    return moment.tz(targetTimezone).year(parseInt(year)).month(parseInt(month)).date(parseInt(dateNum)).startOf("day");
  },
  /**
   * Converts dataString provided in YYYY-MM-DD format to a valid moment date in specific timezone to start of the day.
   * @param {string} dateStr date string in format YYYY-MM-DD
   * @param {string} timezone timezone string according to moment (See {@link https://gist.github.com/diogocapela/12c6617fc87607d11fd62d2a4f42b02a})
   * @returns {moment.Moment | null} return null if date is invalid, otherwise returns moment date object in specific timezone
   */
  getDateFromStringWithTimezone(dateStr, timezone) {
    if (!dateStr || typeof dateStr !== "string" || dateStr.split("-").length !== 3 || !timezone) return null;
    const [year, month, date] = dateStr.split("-");
    return moment
      .tz(timezone)
      .year(parseInt(year))
      .month(parseInt(month) - 1)
      .date(parseInt(date))
      .startOf("day");
  },
  /**
   * Formats date and time in a specific timezone.
   * @param {string} date source date
   * @param {string} timezone timezone string according to moment (See {@link https://gist.github.com/diogocapela/12c6617fc87607d11fd62d2a4f42b02a})
   * @param {boolean} hour24 format time in 24 hours
   * @returns {string} formatted date and time string in target timezone
   */
  dateTimeFormatterWithTimezone: (date, timezone, hour24 = false) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      return tempDate.toLocaleString("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
        hourCycle: hour24 ? "h23" : "h12",
      });
    }
  },
  /**
   * Formats date in a specific timezone.
   * @param {string} date source date
   * @param {string} timezone timezone string according to moment (See {@link https://gist.github.com/diogocapela/12c6617fc87607d11fd62d2a4f42b02a})
   * @returns {string} formatted date string in target timezone
   */
  dateFormatterWithTimezone: (date, timezone) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      return tempDate.toLocaleString("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    }
  },
  /**
   * Formats date to local timezone.
   * @param {string} date source date
   * @returns {string} formatted date string in MM-DD-YY
   */
  dateFormatter: (date) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      let momentDate = moment(date);
      return momentDate.format("MM-DD-YY");
    }
  },
  /**
   * format date to local time in format hh:mm:ss A.
   * @param {string} date source date
   * @returns {string} formatted time string in hh:mm:ss A
   */
  timeFormatter: (date) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      let momentDate = moment(date);
      return momentDate.format("hh:mm:ss A");
    }
  },
  /**
   * format date to local time in format hh:mm A.
   * @param {string} date source date
   * @returns {string} formatted time string in hh:mm A
   */
  timeFormatterWithoutSecs: (date) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      let momentDate = moment(date);
      return momentDate.format("hh:mm A");
    }
  },
  /**
   * format date to local time in format HH:mm (24 hour format).
   * @param {string} date source date
   * @returns {string} formatted time string in HH:mm
   */
  timeFormatter24Hour: (date) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      let momentDate = moment(date);
      return momentDate.format("HH:mm");
    }
  },
  ampmFormatter: (date) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      let momentDate = moment(date);
      return momentDate.format("A");
    }
  },
  timeFormatter12HourWithoutA: (date) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      let momentDate = moment(date);
      return momentDate.format("hh:mm");
    }
  },
  /**
   * Formats date and time to local timezone.
   * @param {string} date source date
   * @returns {string} formatted date string in MM-DD-YY,  hh:mm:ss A
   */
  dateTimeFormatter: (date) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      let momentDate = moment(date);
      return momentDate.format("MM-DD-YY,  hh:mm:ss A");
    }
  },
  /**
   * Formats date and time to local timezone.
   * @param {string} date source date
   * @returns {string} formatted date string in MM-DD-YY,  hh:mm A
   */
  dateTimeFormatterWithoutSecs: (date) => {
    let tempDate = getDate(date);
    if (tempDate === null) {
      return date;
    } else {
      let momentDate = moment(date);
      return momentDate.format("MM-DD-YY,  hh:mm A");
    }
  },
  /**
   * Formats array of string values in to comma separated values
   * @param {string[] | number[]} val array of values to format
   * @returns {string} formatted string
   */
  stringArrayFormatter: (val) => {
    return val !== undefined && Array.isArray(val) ? val.join(", ") : "";
  },
  /**
   * Formats value to string with appropriate checks
   * @param {string | number} val array of values to format
   * @returns {string} formatted string
   */
  stringFormatter: (val) => {
    return val !== undefined ? val.toString() : "";
  },
  /**
   * Formats duration in milliseconds to hh:mm:ss.
   * @param {number} durationInMs duration in milliseconds
   * @param {boolean} includeSeconds include seconds in the formatted time
   * @returns {string} formatted time in hh:mm:ss format
   */
  formatTimeDuration: (durationInMs, includeSeconds = true) => {
    let hours = Math.floor(moment.duration(durationInMs).asHours()) || "00";
    hours = dataFormatters.padZerosBeforeNum(hours, 2);
    return hours + ":" + moment.utc(durationInMs).format(includeSeconds ? "mm:ss" : "mm");
  },
  /**
   * Formats percentage value.
   * @param {number} percentage percentage value
   * @returns {string} formatted percentage value
   */
  formatPercentage: (percentage) => {
    return percentage ? parseFloat(percentage).toFixed(2) : "0";
  },
  /**
   * Pad zeros before a number to make the number equal to no of desired digits.
   * @param {number} num number
   * @param {number} noOfDigits number of digits for target number
   * @returns {string} number in string with padded zeros
   */
  padZerosBeforeNum: (num, noOfDigits) => {
    return num.toString().padStart(noOfDigits, "0");
  },
  /**
   * Convert time from 24 hour format to 12 hour (AM/PM) format.
   * @param {string} time time string in 24 hour format
   * @returns {string} time in 12 hour format
   */
  convert24HourFormattedTimeToAMPM: (time) => {
    if (!time) return null;
    let formattedTime = dataFormatters.timeFormatterWithoutSecs(moment("1/1/2000 " + time).toDate());
    return formattedTime;
  },
  stringifyDateInYYYYMMDD: (date) => {
    return moment(date).format().slice(0, 10);
  },
  stringifyDateInYYYYMMDDWithTimezone: (date, timezone) => {
    return moment.tz(date, timezone).format().slice(0, 10);
  },
  stringifyTimeIn24HourFormat: (date) => {
    return moment(date).format("HH:mm");
  },
  stringifyTimeIn24HourFormatWithTimezone: (date, timezone) => {
    return moment.tz(date, timezone).format("HH:mm");
  },
  formatDateToYYYYMMDD: (orgDate) => {
    let date = new Date(orgDate);
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}${month}${day}`;
  },
};



// WEBPACK FOOTER //
// ./src/utils/dataFormatters.js